/**
 * The sentence primitives shared by the platform answer (apiAnswer) and the
 * endpoint answer (endpointAnswer).
 *
 * Both pages quote the same USD rate for the same lane and describe the same
 * measured window, so the wording lives here once. A second copy would be a
 * second source of truth for a price, which is the one thing these pages are
 * not allowed to disagree about.
 */
import type { BenchmarkSummary } from '../benchmarks';
import { fmtSec, pct, usdCost, usdRate } from '../format';
import { type DisplayPrice, pluralUnit, quotedRateUsd } from '../pricing';
import { m } from '../../paraglide/messages';
import { getLocale } from '../../paraglide/runtime';

export interface AnswerQuestion {
  q: string;
  a: string;
}

/** A USD rate the way the pages quote it: per 1,000 of the billed unit. */
const rate = (price: DisplayPrice): string => usdRate(quotedRateUsd(price));

/** The metered units the gateway publishes, each phrased as a whole rate. */
const UNIT_RATE: Record<string, (inputs: { rate: string }) => string> = {
  request: m.seo_rate_requests,
  result: m.seo_rate_results,
  keyword: m.seo_rate_keywords,
  'audio minute': m.seo_rate_audio_minutes,
  asin: m.seo_rate_asins,
  profile: m.seo_rate_profiles,
};

/** `amount` per 1,000 of the thing it buys: requests, results, keywords, asins.
 *  A flat price is a floor across lanes, so it reads "from". */
const ratePhrase = (price: DisplayPrice, amount: string): string => {
  if (!price.metered) return m.seo_rate_from_requests({ rate: amount });
  if (!price.unit) return m.seo_rate_requests({ rate: amount });
  return UNIT_RATE[price.unit]?.({ rate: amount }) ?? m.seo_rate_unit({ rate: amount, unit: pluralUnit(price.unit) });
};

/**
 * The rate alone, for a title tag: `from $5.50 per 1,000 results`.
 *
 * `usdCost` rather than the `rate` helper below, which is `usdRate` and fixes two
 * decimals: Indeed quotes $0.132 per 1,000 results and `usdRate` prints $0.13, a
 * figure lower than we charge. A rounded price inside a passage is a nuisance; the
 * same rounding in a title tag is the number a searcher decides on, and it has to
 * match the header, which already formats with `usdCost`.
 */
export const titleRate = (price: DisplayPrice): string =>
  ratePhrase(price, usdCost(quotedRateUsd(price)));

/**
 * The rate, in the denomination the offer actually bills in, plus the clause
 * that keeps it honest.
 *
 * A metered offer charges per result, so quoting it per 1,000 requests names a
 * denomination it does not bill in and prints a figure roughly two orders of
 * magnitude above what a call costs. It is quoted per 1,000 of its own unit
 * instead, and the per-request ceiling follows as its own clause - the same
 * split PriceTag renders on screen. Prose that disagreed with the visible price
 * would be a second, wronger account of the same number.
 *
 * `cheapest` lands before the ceiling clause so a platform page can say "on the
 * cheapest endpoint" without cutting the sentence in half.
 */
export const priceClause = (price: DisplayPrice, cheapest = false): string => {
  const quoted = ratePhrase(price, rate(price));
  const head = cheapest ? m.seo_price_cheapest({ price: quoted }) : quoted;
  if (!price.metered) return head;
  return m.seo_price_capped({ price: head, cap: usdCost(price.perRequestUsd) });
};

/** Items as prose in the page language: "product, search, and reviews". */
export const list = (items: string[]): string =>
  new Intl.ListFormat(getLocale(), { type: 'conjunction' }).format(items);

/** Sentences as one passage; Chinese sets them without a space. */
export const sentences = (parts: (string | undefined)[]): string =>
  parts.filter(Boolean).join(getLocale() === 'zh-Hans' ? '' : ' ');

/** Close a sentence that arrived without its own terminal punctuation. */
export const sentence = (text: string): string =>
  /[.!?\u3002\uff01\uff1f]$/.test(text) ? text : `${text}${m.seo_sentence_end()}`;

const seconds = (ms: number): string => m.seo_seconds({ seconds: fmtSec(ms).slice(0, -1) });

/**
 * The measured clause, for whatever `subject` the calling page is about: a
 * platform on /api/:apiId, one endpoint on /api/:apiId/:endpointId.
 *
 * Omitted entirely when nothing has been measured. A sentence whose input is
 * missing is dropped rather than softened, which is the rule the figures
 * themselves follow: no traffic renders a dash, so no traffic writes no
 * sentence.
 */
export function measuredSentence(
  subject: string,
  summary: BenchmarkSummary | null,
): string | undefined {
  if (!summary?.uptimeSample) return undefined;
  const inputs = {
    subject,
    uptime: pct(summary.uptimePct),
    calls: summary.uptimeSample.toLocaleString(getLocale() === 'en' ? 'en-US' : getLocale()),
  };
  if (!summary.latencySample || !summary.latencyP50Ms) return m.seo_measured(inputs);
  const duration = seconds(summary.latencyP50Ms);
  return summary.latencyIsEstimate
    ? m.seo_measured_latency_weighted({ ...inputs, duration })
    : m.seo_measured_latency({ ...inputs, duration });
}
